import { useContext } from '@berlin-front/common/composables/context';
import { BerlinBffContext } from '@services/BerlinBff';
import { readonly, reactive, unref, watch, ref, computed } from '@vue/composition-api';
import { useMutation } from 'vue-query';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { useRoute } from '@berlin-front/common/composables/useRoute';
import { usePrepaymentCheck } from '@services/service-type-bridge/atone-common';
import { event } from 'vue-gtag';
import { SERVICE_TYPE } from '@/services/ModalParameterContext';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep, getRequiredRefValueByPath, notNil } from '@berlin-front/common/utils';
import { broadcast } from '@/broadcast';
import {
  // AtoneCapturePostRequestSalesFormEnum,
  // AtoneCapturePostRequestTransactionOptionsEnum,
  // AtoneSplitCapturePostRequestSalesFormEnum,
  AtoneSplitCapturePostRequestSplitTimesEnum,
  // AtoneSplitCapturePostRequestTransactionOptionsEnum,
  AtoneSplitCapturePostResponseAuthorizationResultEnum,
} from '@netprotections/berlin-bff';
import useIsLegacy from '@composables/useIsLegacy';
import { ModalBridgeContext } from '@services/ModalBridgeContext';

const SETTLEMENT_TIMEOUT = 20000;

// 手っ取り早くanyにしちゃったのでコメントアウト (BFF側でEnumを共通化するンゴ！)
// const transactionOption: Record<
//   AtoneCapturePostRequestTransactionOptionsEnum,
//   AtoneSplitCapturePostRequestTransactionOptionsEnum
// > = {
//   // eslint-disable-next-line no-underscore-dangle
//   [AtoneCapturePostRequestTransactionOptionsEnum._01]: AtoneSplitCapturePostRequestTransactionOptionsEnum._01,
//   // eslint-disable-next-line no-underscore-dangle
//   [AtoneCapturePostRequestTransactionOptionsEnum._02]: AtoneSplitCapturePostRequestTransactionOptionsEnum._02,
//   // eslint-disable-next-line no-underscore-dangle
//   [AtoneCapturePostRequestTransactionOptionsEnum._03]: AtoneSplitCapturePostRequestTransactionOptionsEnum._03,
// };

// const salesForm: Record<AtoneCapturePostRequestSalesFormEnum, AtoneSplitCapturePostRequestSalesFormEnum> = {
//   [AtoneCapturePostRequestSalesFormEnum.NUMBER_1]: AtoneSplitCapturePostRequestSalesFormEnum.NUMBER_1,
//   [AtoneCapturePostRequestSalesFormEnum.NUMBER_2]: AtoneSplitCapturePostRequestSalesFormEnum.NUMBER_2,
//   [AtoneCapturePostRequestSalesFormEnum.NUMBER_3]: AtoneSplitCapturePostRequestSalesFormEnum.NUMBER_3,
//   [AtoneCapturePostRequestSalesFormEnum.NUMBER_4]: AtoneSplitCapturePostRequestSalesFormEnum.NUMBER_4,
//   [AtoneCapturePostRequestSalesFormEnum.NUMBER_5]: AtoneSplitCapturePostRequestSalesFormEnum.NUMBER_5,
//   [AtoneCapturePostRequestSalesFormEnum.NUMBER_6]: AtoneSplitCapturePostRequestSalesFormEnum.NUMBER_6,
// };

export const useLoading = () => ({
  useLoading: () => {
    const [isLegacy] = useIsLegacy();
    const router = useRouter();
    const route = useRoute();
    const subtractPoint = computed(() => Number.parseInt(route.params.point || '0', 10));
    const { payment, js } = useContext(BerlinBffContext);
    const prePaymentCheck = usePrepaymentCheck();
    const { atoneSplitCapturePostResult, setAtoneSplitCapturePostResult, tokens, atoneModalParameter } =
      useContext(ApiCacheContext);
    const globals = useContext(ModalBridgeContext);
    const completeProgress = ref(false);
    const nextPagePath = ref('');
    const requireJsPayment = ref(true);

    // prepaymentチェック。
    const { mutate: check, isSuccess: isSuccessPrepaymentCheck } = useMutation('prepaymentCheck', async () =>
      prePaymentCheck(),
    );
    check();

    const {
      isSuccess: isSuccessJsPayment,
      isError: isErrorJsPayment,
      mutate: mutateJsPaymentCapture,
    } = useMutation(
      'atone.JsPaymentCapturePost',
      () =>
        js.atoneJavascriptControllerJavascriptPaymentCapture({
          id: getRefDeep(atoneSplitCapturePostResult, 'id'),
        }),
      {},
    );

    // APIの呼び出し(submit)
    const { isSuccess: isSuccessPayment, mutate } = useMutation(
      'atone.SplitPaymentCapturePost',
      () =>
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        payment.atonePaymentControllerSplitCapturePost(
          notNil(tokens.value?.npToken),
          atoneModalParameter.value.pubKey,
          {
            amount: atoneModalParameter.value.payment.amount,
            splitTimes: route.query.splitPattern as AtoneSplitCapturePostRequestSplitTimesEnum,
            checksum: atoneModalParameter.value.payment.checksum,
            items: notNil(atoneModalParameter.value.payment.items),
            trackToken: notNil(tokens.value?.trustedTrackToken),
            shopTransactionNo: atoneModalParameter.value.payment.shopTransactionNo,
            subtractPoint: unref(subtractPoint),
            // TODO: any変換をブチとる
            customer: (atoneModalParameter.value.payment.customer as unknown as any) ?? undefined, // eslint-disable-line @typescript-eslint/no-explicit-any
            destCustomers: atoneModalParameter.value.payment.destCustomers,
            userNo: atoneModalParameter.value.payment.userNo,
            transactionOptions: atoneModalParameter.value.payment.transactionOptions as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any,
            serviceSupplier: atoneModalParameter.value.payment.serviceSupplier as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any,
            salesSettled: atoneModalParameter.value.payment.salesSettled,
            description: atoneModalParameter.value.payment.description,
            salesForm: atoneModalParameter.value.payment.salesForm as unknown as any, // eslint-disable-line @typescript-eslint/no-explicit-any,
            sourceMedia: atoneModalParameter.value.payment.sourceMedia,
            isOldParameter: isLegacy.value,
            requestId: globals.requestId,
          },
          atoneModalParameter?.value?.terminalId,
        ),
      {
        retry: false,
        onSuccess: ({ data }) => {
          setAtoneSplitCapturePostResult(data);
          const { authorizationResult, additionalAuthorizationResultNgReason } = data;
          if (authorizationResult === AtoneSplitCapturePostResponseAuthorizationResultEnum.NUMBER_1) {
            // 決済成功。
            mutateJsPaymentCapture();
            // newrelic(決済成功)
            window.newrelic.addPageAction('payment_complete', {
              revenue: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.amount'),
              coupon: unref(subtractPoint),
              products: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.items').map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
              shop_transaction_no: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.shopTransactionNo'),
            });

            event('purchase', {
              transaction_id: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.shopTransactionNo'),
              value: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.amount'),
              coupon: unref(subtractPoint),
              currency: 'JPY',
              items: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.items').map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
            });
          } else {
            // 決済失敗。
            requireJsPayment.value = false;
            if (additionalAuthorizationResultNgReason === 1) {
              // 公的証明書確認画面を表示
              nextPagePath.value = '/atone/fail/official-identification';
            } else if (additionalAuthorizationResultNgReason === 2) {
              // アプリ認証画面を表示
              nextPagePath.value = '/atone/fail/spapp-login';
            } else {
              // 与信失敗画面を表示
              nextPagePath.value = '/atone/fail/auth-ng';
            }
            // newrelic(決済ng)
            window.newrelic.addPageAction('payment_ng', {
              revenue: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.amount'),
              coupon: unref(subtractPoint),
              products: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.items').map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
              shop_transaction_no: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.shopTransactionNo'),
            });

            event('exception', {
              description: 'payment_ng',
              fatal: false,
              transaction_id: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.shopTransactionNo'),
              value: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.amount'),
              coupon: unref(subtractPoint),
              currency: 'JPY',
              items: getRequiredRefValueByPath({ atoneModalParameter }, 'payment.items').map((item) => ({
                id: item.shopItemId,
                price: item.itemPrice,
                name: item.itemName,
                quantity: item.itemCount,
              })),
            });
          }
        },
      },
    );
    watch(isSuccessPrepaymentCheck, (isSuccess) => {
      if (isSuccess) {
        mutate();
      }
    });

    watch([completeProgress, nextPagePath], () => {
      const complete = unref(completeProgress);
      const path = unref(nextPagePath);
      if (complete) {
        if (
          unref(atoneSplitCapturePostResult)?.authorizationResult ===
          AtoneSplitCapturePostResponseAuthorizationResultEnum.NUMBER_1
        ) {
          broadcast.send({
            type: 'MODAL_CONTROL_SUCCEEDED',
            payload: {
              serviceType: SERVICE_TYPE.ATONE,
            },
          });
        }
        if (path) {
          void router.replace({ path });
        }
      }
    });

    // 処理の終了。
    const isCompleted = computed(() => {
      if (unref(requireJsPayment)) {
        return unref(isSuccessPayment) && (unref(isSuccessJsPayment) || unref(isErrorJsPayment));
      }
      return unref(isSuccessPayment);
    });
    const isLoading = computed(() => !unref(isCompleted));

    return readonly(
      reactive({
        title: '注文確定',
        onComplete: () => {
          completeProgress.value = true;
        },
        isLoading,
        isCompleted,
        duration: SETTLEMENT_TIMEOUT,
      }),
    );
  },
});
